import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { LuPanelLeft } from 'react-icons/lu';
import useSchoolStore from '../store/schoolStore';
import useUserStore from '../store/userStore';
import useGeneralStore from '../store/generalStore';
import useChatStore from '../store/chatStore';
import SidebarNav from '../components/layout/SidebarNav';
import AccountMenu from '../components/layout/AccountMenu';
import AskMeBar from '../components/layout/AskMeBar';
import UpdateDialog from '../components/utils/UpdateDialog';

const SIDEBAR_STORAGE_KEY = 'schoolline-sidebar';

// The folded / open menu is a per-browser convenience; storage can be blocked, so reads never throw.
// With no saved choice, the menu starts folded on narrow screens so the page keeps its width.
const readSidebarCollapsed = () => {
  const isNarrowScreen = window.innerWidth < 1100;
  try {
    const savedState = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (!savedState) {
      return isNarrowScreen;
    }
    return savedState === 'collapsed';
  } catch (error) {
    return isNarrowScreen;
  }
};

const saveSidebarCollapsed = (isCollapsed) => {
  try {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, isCollapsed ? 'collapsed' : 'open');
  } catch (error) {
    // Nothing to do: the menu simply opens next time
  }
};

/**
 * The dashboard shell for every page under /dash/:schoolId.
 * Top bar: menu toggle, school, the Ask Me box and the account menu.
 * Left: the main menu (SidebarNav). Right: the page, rendered through <Outlet />.
 */
const DashboardLayout = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(readSidebarCollapsed);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const { schoolData, schoolId, getSchoolData } = useSchoolStore();
  const { getCurrentUser } = useUserStore();
  const { checkUpdate, hasUpdate } = useGeneralStore();
  const { subscribeToChats, unsubscribeFromChats, getUserChats } = useChatStore();
  const location = useLocation();

  const isAskMePage = location.pathname.startsWith(`/dash/${schoolId}/ask`);

  useEffect(() => {
    checkUpdate();
    getCurrentUser();
    getSchoolData();
    // Once when the dashboard opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasUpdate) {
      setShowUpdateDialog(true);
    }
  }, [hasUpdate]);

  // The Ask Me history stays live (useChatStore.subscribeToChats) wherever the user is
  useEffect(() => {
    subscribeToChats();
    return () => {
      unsubscribeFromChats();
    };
  }, [subscribeToChats, unsubscribeFromChats]);

  useEffect(() => {
    getUserChats();
  }, [getUserChats]);

  const handleToggleSidebar = () => {
    const isCollapsed = !isSidebarCollapsed;
    setIsSidebarCollapsed(isCollapsed);
    saveSidebarCollapsed(isCollapsed);
  };

  return (
    <div className="flex h-screen flex-col bg-ui-page text-ui-ink">
      <UpdateDialog isOpen={showUpdateDialog} onClose={() => setShowUpdateDialog(false)} />

      <header className="z-30 flex h-14 shrink-0 items-center gap-3 border-b border-ui-line bg-ui-card px-3">
        <button
          type="button"
          onClick={handleToggleSidebar}
          aria-label={isSidebarCollapsed ? 'Show the menu' : 'Fold the menu'}
          title={isSidebarCollapsed ? 'Show the menu' : 'Fold the menu'}
          className="flex size-9 shrink-0 items-center justify-center rounded-lg text-ui-ink-3 transition-colors hover:bg-ui-hover hover:text-ui-ink"
        >
          <LuPanelLeft aria-hidden className="size-5" />
        </button>

        <Link to={`/dash/${schoolId}`} className="flex min-w-0 shrink items-center gap-2.5 rounded-lg pr-2">
          <img src={schoolData?.iconUrl || '/assets/logo.png'} alt="" className="size-8 shrink-0 rounded-md object-contain" />
          <span className="min-w-0">
            <span className="block truncate text-[15px] leading-tight font-semibold text-ui-ink">
              {schoolData?.name || 'SchoolLine'}
            </span>
            <span className="hidden text-[11px] leading-tight text-ui-ink-3 lg:block">SchoolLine dashboard</span>
          </span>
        </Link>

        <div className="flex min-w-56 flex-1 justify-center px-2 lg:px-4">
          {!isAskMePage && (
            <div className="w-full max-w-xl">
              <AskMeBar schoolId={schoolId} />
            </div>
          )}
        </div>

        <AccountMenu />
      </header>

      <div className="flex min-h-0 flex-1">
        <aside
          className={`shrink-0 border-r border-ui-line bg-ui-card transition-[width] duration-200 ease-out ${
            isSidebarCollapsed ? 'w-16' : 'w-60'
          }`}
        >
          <SidebarNav schoolId={schoolId} isCollapsed={isSidebarCollapsed} />
        </aside>

        <main id="dashboard-main-content" className="ui-scroll min-w-0 flex-1 overflow-y-auto">
          <div key={location.pathname} className={isAskMePage ? 'h-full' : 'animate-enter px-5 py-6 lg:px-8'}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
